<script setup lang="ts">
import { computed, type PropType, reactive, ref, watch } from 'vue'
import { useLoadInventObjectSubType, useLoadInventObjectSubTypes } from './../../model/api.ts'
import { useRoute } from 'vue-router'
import { VSelectSearch } from '@/shared/ui'
import {
  ComputedConditionList,
  ComputedTypeList,
  DateFieldList,
} from '@/entities/Inventory/TypeField'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { WObjectSubTypeSelect } from '@/widgets'

const props = defineProps({
  groups: {
    type: Array,
    default: () => [],
  },
  field: {
    type: Object as PropType<ObjectInventoryField>,
    required: true,
  },
  /** Hide «Перечень» / field selects when list + field are implied by context (e.g. passport summary). */
  hideListAndFieldSelectors: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const emits = defineEmits(['update'])

/** Вычисляем тип перечня для группы */
const listObjectTypeId = computed(() => {
  if (!props.field?.group) return null

  const group: any = props.groups.find((item: any) => item.id === props.field.group)

  return group?.list_invent_object_type_id || null
})

const currentValues = reactive({
  list_id: props.field?.computed_settings?.list_id || listObjectTypeId.value,
  current_field_id: props.field?.computed_settings?.current_field_id || null,
  optional_field_id: props.field?.computed_settings?.optional_field_id || null,
  computed_type_id: props.field?.computed_settings?.computed_type_id || null,
  computed_condition_id: props.field?.computed_settings?.computed_condition_id || null,
  condition_value: props.field?.computed_settings?.condition_value || null,
  condition_value_range: props.field?.computed_settings?.condition_value_range || {
    min: 0,
    max: 0,
  },
  condition_value_in_list: props.field?.computed_settings?.condition_value_in_list || null,
})

const useCurrentField = ref<boolean>(true)
const objectId = route.params['object_id'] as string

/** Вычисляем список полей перечня */
const { data, isPending } = useLoadInventObjectSubType(
  computed(() => currentValues.list_id),
  objectId,
)

const fieldList = computed(() => {
  if (!data.value) return []

  return data.value.data.fields
})

/** Вычисляем разрешенные типы вычисления для определенных типов полей */
const typeList = computed(() => {
  if (!currentValues.current_field_id) return []

  const type = fieldList.value.find(
    (item: { id: string }) => item.id === currentValues.current_field_id,
  )

  if (!type) return []

  return ComputedTypeList.filter((item: any) => {
    return item.accept_fields.includes(type.type)
  })
})

/** Вычисляем тип объекта для поля типа список **/
const objectTypeId = computed(() => {
  if (!currentValues.current_field_id && !currentValues.optional_field_id) return null

  const type = fieldList.value.find(
    (item) => item.id === (currentValues.optional_field_id || currentValues.current_field_id),
  )

  return type?.object_type || null
})

/** Вычисляем разрешенные условия для определенных типов полей */
const conditionList = computed(() => {
  if (!currentValues.current_field_id && !currentValues.optional_field_id) return []

  const type = fieldList.value.find(
    (item) => item.id === (currentValues.optional_field_id || currentValues.current_field_id),
  )

  if (!type) return []

  return ComputedConditionList.filter((item: any) => {
    return item.accept_fields.includes(type.type)
  })
})

/** Вычисляем данные списка IN_LIST */
const { data: subData, isPending: subIsPending } = useLoadInventObjectSubTypes(
  objectTypeId,
  objectId,
)

const objectTypeList = computed(() => {
  if (!subData.value) return []

  return subData.value.data.sub_types.map((item) => ({
    title: item.name,
    value: item.id,
  }))
})

/** Обновляем данные поля */
watch(currentValues, () => {
  emits('update', { ...props.field, computed_settings: currentValues })
})

/** Вычисляем является ли выбранное поле датой */
const isDate = computed(() => {
  if (!currentValues.current_field_id && !currentValues.optional_field_id) return ''

  const type = fieldList.value.find(
    (item) => item.id === (currentValues.optional_field_id || currentValues.current_field_id),
  )

  if (!type) return ''

  return Object.values(DateFieldList).includes(type.type as DateFieldList)
})
</script>

<template>
  <template v-if="!hideListAndFieldSelectors">
    <WObjectSubTypeSelect :label="$t('itf.select_list')" v-model="currentValues.list_id" />

    <VSelectSearch
      v-if="fieldList.length"
      :loading="isPending"
      :items="fieldList"
      :label="$t('itf.select_field')"
      item-title="name"
      item-value="id"
      v-model="currentValues.current_field_id"
    />
  </template>

  <VSelect
    v-if="currentValues.current_field_id"
    :items="typeList"
    :label="$t('itf.computed_type')"
    v-model="currentValues.computed_type_id"
  />

  <template v-if="currentValues.computed_type_id">
    <VCheckbox class="ml-2 mb-4" v-model="useCurrentField" :label="$t('itf.use_current_field')" />

    <VSelectSearch
      v-if="!useCurrentField"
      :items="fieldList"
      :label="$t('itf.select_field')"
      item-title="name"
      item-value="id"
      v-model="currentValues.optional_field_id"
    />

    <VSelect
      :items="conditionList"
      :label="$t('itf.computed_condition')"
      v-model="currentValues.computed_condition_id"
    />

    <VTextField
      :type="isDate ? 'date' : 'text'"
      v-if="
        ['MORE', 'LESS', 'EQUAL', 'INCLUDE'].includes(String(currentValues.computed_condition_id))
      "
      :label="$t('itf.value')"
      v-model="currentValues.condition_value"
    />

    <div
      v-if="['RANGE'].includes(String(currentValues.computed_condition_id))"
      class="invent-type-field__range-grid"
    >
      <VTextField
        :type="isDate ? 'date' : 'text'"
        v-model="currentValues.condition_value_range.min"
        :label="$t('itf.from')"
      />
      <VTextField
        :type="isDate ? 'date' : 'text'"
        v-model="currentValues.condition_value_range.max"
        :label="$t('itf.to')"
      />
    </div>

    <VSelectSearch
      v-if="['IN_LIST'].includes(String(currentValues.computed_condition_id))"
      :loading="subIsPending"
      :items="objectTypeList"
      v-model="currentValues.condition_value_in_list"
      multiple
    />
  </template>
</template>
